<template>
  <BaseModal :modalTitle="$t('area.drop-point.private.create.title')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="address"
        :label="$t('form.label.address')"
        :rules="['required']"
        isRequired
        v-model="form.address">
        <template v-if="isMagicAddressEnabled" #trailingIcon>
          <MagicAddressButton
            :currentAddressLine="form.address"
            @on-fill="onMagicAddress" />
        </template>
      </UiInputTextFieldVee>
      <div class="grid grid-cols-2 gap-4">
        <UiInputTextFieldVee
          name="postalCode"
          :label="$t('form.label.postal-code')"
          :rules="['required']"
          isRequired
          v-model="form.postalCode" />
        <UiInputTextFieldVee
          name="city"
          :label="$t('form.label.city')"
          :rules="['required']"
          isRequired
          v-model="form.city" />
      </div>
      <UiInputTextFieldVee
        name="placement"
        :label="$t('area.drop-point.field.placement')"
        :placeholder="$t('area.drop-point.placeholder.placement-private')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.placement" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { canUseMagicAddress } from '@/dev/magicAddress'
import type { IMagicAddress } from '@/dev/magicAddress'
import { useDropPointStore } from '@/stores/dropPointStore'
import { useModalStore } from '@/stores/modalStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { reportUnexpectedError } from '@/globals/typescript/utils/reportError'
import { mapActions, mapState } from 'pinia'
import type { PropType } from 'vue'
import type { ICustomerRef } from '@team-milva/lab-models/customer'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'CreatePrivateDropPointModal',

  props: {
    /** The business or insurance customer the drop point is created on (MIL-402). */
    customer: {
      type: Object as PropType<ICustomerRef>,
      required: true,
    },
  },

  data() {
    return {
      isMagicAddressEnabled: canUseMagicAddress(),
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        address: '',
        postalCode: '',
        city: '',
        placement: '',
      },
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
  },

  methods: {
    onMagicAddress(address: IMagicAddress) {
      this.form.address = address.addressLine
      this.form.postalCode = address.postalCode
      this.form.city = address.city
    },

    ...mapActions(useDropPointStore, ['createPrivateDropPoint']),
    ...mapActions(useModalStore, ['closeTop']),

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.createPrivateDropPoint({
          customer: this.customer,
          address: this.form.address,
          postalCode: this.form.postalCode,
          city: this.form.city,
          placement: this.form.placement,
          activeState: 'active',
        })
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Privat droppested oprettet'))
        this.closeTop()
      } catch (error) {
        reportUnexpectedError(error, { operation: 'private-drop-point-create' })
        useToast().error(this.$t('W: Kunne ikke oprette privat droppested'))
        this.errorText = this.$t('error.common.standard') as string
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

